// ** React Imports
import * as React from 'react'
import { useUser } from 'src/lib/contexts/UserContext'
import { AnimatePresence, motion } from 'motion/react'
import { classNames } from 'src/lib/utils/helpers'
import { useLocale } from 'src/lib/hooks/useLocal'

interface SaveNotificationProps {
  onSave: any
  onDiscard: any
  isVisible: boolean
  isActive?: boolean
  isLoading?: boolean
}
const SaveNotification: React.FC<React.HTMLAttributes<HTMLDivElement> & SaveNotificationProps> = ({
  onSave,
  onDiscard,
  isVisible,
  isActive = false,
  isLoading = false
}) => {
  const { user } = useUser()
  const { t } = useLocale()

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ y: 100, scale: 0.96, filter: 'blur(4px)' }}
          animate={{ y: 0, scale: 1, filter: 'blur(0px)' }}
          exit={{ y: 100, scale: 0.96, filter: 'blur(4px)' }}
          transition={{
            type: 'spring',
            mass: 1,
            stiffness: 500,
            damping: 30
          }}
          className='fixed bottom-6 inset-x-0 mx-auto w-[480px] h-12 pl-6 pr-4 flex items-center justify-between bg-zinc-800 rounded-full z-[1000] shadow-[0_0_0_1px_#27272A_inset,0_54px_80px_-5px_rgba(0,0,0,0.20),0_12px_20px_0_rgba(0,0,0,0.20),0_4px_10px_-2px_rgba(0,0,0,0.20),0_0_0_2px_rgba(255,255,255,0.24)_inset]'
        >
          <div className='flex items-center justify-between w-full gap-4'>
            <p className='text-white font-medium text-sm'>{t('has_unsaved_changes')}</p>
            <div className='flex items-center gap-2'>
              <button
                id='notification-discard-button'
                type='button'
                className='w-fit h-7 justify-center items-center px-4 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors'
                onClick={onDiscard}
                disabled={isLoading}
              >
                {t('discard')}
              </button>
              <button
                disabled={isLoading || (isActive ? false : user?.isDemoUser)}
                id='notification-save-button'
                type='submit'
                className={classNames(
                  'w-fit h-7 px-4 text-sm font-medium rounded-md transition-colors flex items-center justify-center',
                  user?.isDemoUser && !isActive
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-primary-dark text-white hover:bg-primary-dark/90',
                  isLoading ? 'opacity-70 cursor-not-allowed' : ''
                )}
                onClick={onSave}
              >
                {isLoading ? (
                  <span className='inline-flex items-center gap-2'>
                    <span className='block w-4 h-4 border-2 border-white/60 border-t-white rounded-full animate-spin' />
                    <span>{t('saving') ?? 'Saving...'}</span>
                  </span>
                ) : (
                  t('save')
                )}
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}

export default SaveNotification
